<template>
  <div class="contract-sign-proposal m-0 text-left">
    <div class="csp-scroll">
    <!-- Étape 1 -->
    <template v-if="profileData && signFlowStep === 1">
      <div class="csp-billing">
        <div class="csp-header-row d-flex align-items-start justify-content-between mb-2">
          <div class="d-flex flex-wrap align-items-center">
            <span class="csp-badge">Étape 1 sur 2</span>
            <span class="csp-badge-sub text-muted small ml-2">Validation de l'offre</span>
          </div>
        </div>
        <h4 class="csp-title font-weight-bold mb-2">Vos informations de facturation</h4>
        <p class="csp-desc text-muted small mb-4">
          Renseignez les coordonnées qui apparaîtront sur vos factures.
        </p>

        <p class="csp-section-label text-muted text-uppercase small font-weight-bold mb-3">
          Adresse de facturation
        </p>

        <div class="form-group mb-3">
          <label class="csp-label">Adresse <span class="text-danger">*</span></label>
          <input
            v-model="model.address"
            type="text"
            class="form-control csp-input"
            placeholder="Numéro et nom de rue"
            autocomplete="street-address"
            @blur="touchField('address')"
          />
          <div v-if="showRequired('address')" class="csp-error small mt-1">Requis</div>
        </div>

        <div class="form-row">
          <div class="form-group col-md-4 mb-3">
            <label class="csp-label">Code postal <span class="text-danger">*</span></label>
            <input
              v-model="model.code_postal"
              type="text"
              class="form-control csp-input"
              :maxlength="isFrance ? 5 : undefined"
              placeholder="75001"
              autocomplete="postal-code"
              @input="onPostalInput"
              @blur="touchField('code_postal')"
            />
            <div v-if="showRequired('code_postal')" class="csp-error small mt-1">Requis</div>
            <div
              v-else-if="isFrance && model.code_postal && !isFrancePostalValid"
              class="csp-error small mt-1"
            >
              Le code postal doit contenir 5 chiffres
            </div>
          </div>
          <div class="form-group col-md-8 mb-3">
            <label class="csp-label">Ville <span class="text-danger">*</span></label>
            <input
              v-model="model.city"
              type="text"
              class="form-control csp-input"
              placeholder="Paris"
              autocomplete="address-level2"
              @blur="touchField('city')"
            />
            <div v-if="showRequired('city')" class="csp-error small mt-1">Requis</div>
          </div>
        </div>

        <div class="form-group mb-3">
          <label class="csp-label">Pays <span class="text-danger">*</span></label>
          <select
            v-model="model.country_id"
            class="form-control csp-input"
            @blur="touchField('country_id')"
            @change="onCountryChange"
          >
            <option disabled value="">—</option>
            <option v-for="c in orderedCountries" :key="c.id" :value="String(c.id)">
              {{ frenchCountryLabel(c) }}
            </option>
          </select>
          <div v-if="showRequired('country_id')" class="csp-error small mt-1">Requis</div>
        </div>

        <div v-if="isFrance" class="form-group mb-3">
          <label class="csp-label">Numéro de TVA intracommunautaire <span class="text-danger">*</span></label>
          <input
            v-model="model.vat_number"
            type="text"
            class="form-control csp-input csp-vat-input"
            maxlength="13"
            placeholder="FR12345678901"
            autocomplete="off"
            @input="onVatNumberInput"
            @blur="touchField('vat_number')"
          />
          <div v-if="showVatError" class="csp-error small mt-1">{{ frenchVatError(model.vat_number) }}</div>
        </div>
      </div>

      <span
        class="loadingres d-flex p-2 spinner-border loading-spinner mx-auto"
        v-if="loading"
      ></span>
      <div v-if="showerror && !loading">
        <h4 class="text-danger small">
          Erreur : votre carte n'a pas été autorisée
        </h4>
      </div>

      <template v-if="bank_transfer_enabled != 1">
        <p class="csp-section-label text-muted text-uppercase small font-weight-bold mb-3 mt-2">
          Mode de paiement
        </p>

        <div
          class="csp-pay-card mb-2"
          :class="{ 'csp-pay-card--active': paymentMethod === 'sepa' }"
          role="button"
          tabindex="0"
          @click="paymentMethod = 'sepa'"
          @keydown.enter.prevent="paymentMethod = 'sepa'"
          @keydown.space.prevent="paymentMethod = 'sepa'"
        >
          <div class="d-flex align-items-start">
            <span class="csp-radio mr-3" :class="{ 'csp-radio--on': paymentMethod === 'sepa' }" />
            <div class="flex-grow-1">
              <div class="d-flex flex-wrap align-items-center">
                <span class="font-weight-bold">Prélèvement SEPA</span>
                <span class="csp-reco-badge ml-2">Recommandé</span>
              </div>
              <p class="text-muted small mb-0 mt-1">
                Aucun frais de transaction. Idéal pour les abonnements récurrents.
              </p>
            </div>
          </div>
        </div>

        <div
          class="csp-pay-card csp-pay-card--outline mb-4"
          :class="{ 'csp-pay-card--active': paymentMethod === 'creditCard' }"
          role="button"
          tabindex="0"
          @click="paymentMethod = 'creditCard'"
          @keydown.enter.prevent="paymentMethod = 'creditCard'"
          @keydown.space.prevent="paymentMethod = 'creditCard'"
        >
          <div class="d-flex align-items-start">
            <span class="csp-radio mr-3" :class="{ 'csp-radio--on': paymentMethod === 'creditCard' }" />
            <div class="flex-grow-1">
              <div class="d-flex flex-wrap align-items-center justify-content-between">
                <span class="font-weight-bold">Carte bancaire</span>
                <div class="csp-card-logos d-flex align-items-center ml-2">
                  <img :src="visaLogo" alt="Visa" class="csp-logo-img" />
                  <img :src="mcLogo" alt="Mastercard" class="csp-logo-img" />
                  <img :src="cbLogo" alt="CB" class="csp-logo-img" />
                </div>
              </div>
              <p class="text-muted small mb-0 mt-1">
                Paiement sécurisé via Viva.com. Visa, Mastercard, CB acceptés.
              </p>
            </div>
          </div>
        </div>

        <div class="csp-footer border-top pt-3 mt-2 d-flex align-items-center justify-content-between">
          <button type="button" class="btn btn-link text-muted p-0 csp-link-cancel" @click="closeCreditCardModal">
            Annuler
          </button>
          <button
            type="button"
            class="btn csp-btn-continue text-white"
            :disabled="!canContinue"
            @click="onContinue"
          >
            Continuer →
          </button>
        </div>
      </template>

      <div v-else class="row mt-1 mb-0 pt-3">
        <div class="col-12 d-flex justify-content-center">
          <button
            class="btn btn-primary themed-button ml-2"
            @click="optionBankTransfer()"
            type="submit"
          >
            <span class="btn-width" v-if="btnMethods.bankTransfer.loading">
              <Loadingcircle :paramloading="paramloading" />
            </span>
            <span v-else>
              <span class="icon-banknote mx-1"></span>
              Virement bancaire
            </span>
          </button>
        </div>
        <div class="col-12 d-flex justify-content-center mt-2">
          <p class="text-warning">
            * {{ bank_transfer_contractual_days }}
            jours de délai contractuel pour le virement
          </p>
        </div>
      </div>
    </template>

    <!-- Étape 2 : mandat SEPA (même conteneur modal parent, sans BankAccountForm) -->
    <template v-else-if="profileData && signFlowStep === 2">
      <div class="csp-sepa">
        <div class="csp-header-row d-flex align-items-start justify-content-between mb-1">
          <div class="d-flex flex-wrap align-items-center">
            <span class="csp-badge">Étape 2 sur 2</span>
            <span class="csp-badge-sub text-muted small ml-2">Mandat SEPA</span>
          </div>
        </div>
        <h4 class="csp-title csp-sepa-title font-weight-bold mb-1">Vos coordonnées bancaires</h4>
        <p class="csp-desc text-muted small mb-2">
          Renseignez votre IBAN pour autoriser Oliverlist à prélever vos factures.
        </p>

        <p class="csp-section-label csp-sepa-section text-muted text-uppercase small font-weight-bold mb-2">
          Compte bancaire
        </p>

        <div class="form-group mb-2">
          <label class="csp-label">Nom de votre entreprise <span class="text-danger">*</span></label>
          <input
            v-model="model.bank_account_name"
            type="text"
            class="form-control csp-input"
            placeholder="Aivoni"
            autocomplete="organization"
            @blur="touchSepaField('bank_account_name')"
          />
          <div v-if="showSepaRequired('bank_account_name')" class="csp-error small mt-1">Requis</div>
        </div>

        <div class="form-group mb-2">
          <label class="csp-label">IBAN <span class="text-danger">*</span></label>
          <input
            :value="ibanFormatted"
            type="text"
            class="form-control csp-input csp-iban-input"
            placeholder="FR76 1234 5678 9012 3456 7890 123"
            spellcheck="false"
            autocomplete="off"
            @input="onIbanInput"
            @blur="touchSepaField('iban')"
          />
          <div v-if="showSepaRequired('iban')" class="csp-error small mt-1">Requis</div>
        </div>

        <div class="form-group mb-2">
          <label class="csp-label">BIC / SWIFT <span class="text-danger">*</span></label>
          <input
            v-model="model.swift_bic"
            type="text"
            class="form-control csp-input"
            placeholder="BNPAFRPPXXX"
            spellcheck="false"
            autocomplete="off"
            @input="onSwiftInput"
            @blur="touchSepaField('swift_bic')"
          />
          <div v-if="showSepaRequired('swift_bic')" class="csp-error small mt-1">Requis</div>
        </div>

        <div class="csp-info-box d-flex align-items-start mb-2">
          <i class="icon-refresh mr-2 mt-1 text-muted" aria-hidden="true"></i>
          <div class="small">
            <span class="text-muted">Type de prélèvement :</span>
            <span class="font-weight-bold text-body"> Récurrent mensuel</span>
          </div>
        </div>

        <div class="d-flex flex-wrap align-items-center justify-content-between mb-1">
          <p class="csp-section-label csp-sepa-section text-muted text-uppercase small font-weight-bold mb-0">
            Adresse de facturation
          </p>
          <div class="form-check mb-0">
            <input
              id="csp-billing-same"
              v-model="billingSameAsCompany"
              class="form-check-input"
              type="checkbox"
            />
            <label class="form-check-label small font-weight-bold" for="csp-billing-same">
              Identique à l'entreprise
            </label>
          </div>
        </div>

        <div class="form-group mb-2">
          <label class="csp-label">Adresse <span class="text-danger">*</span></label>
          <input
            v-model="model.address"
            type="text"
            class="form-control csp-input"
            :class="{ 'csp-input--locked': billingSameAsCompany }"
            placeholder="Numéro et nom de rue"
            :disabled="billingSameAsCompany"
            @blur="touchSepaField('address')"
          />
          <div v-if="showSepaRequired('address')" class="csp-error small mt-1">Requis</div>
        </div>

        <div class="form-row">
          <div class="form-group col-md-4 mb-2">
            <label class="csp-label">Code postal <span class="text-danger">*</span></label>
            <input
              v-model="model.code_postal"
              type="text"
              class="form-control csp-input"
              :class="{ 'csp-input--locked': billingSameAsCompany }"
              :maxlength="isFrance ? 5 : undefined"
              placeholder="75001"
              :disabled="billingSameAsCompany"
              @input="onSepaPostalInput"
              @blur="touchSepaField('code_postal')"
            />
            <div v-if="showSepaRequired('code_postal')" class="csp-error small mt-1">Requis</div>
          </div>
          <div class="form-group col-md-8 mb-2">
            <label class="csp-label">Ville <span class="text-danger">*</span></label>
            <input
              v-model="model.city"
              type="text"
              class="form-control csp-input"
              :class="{ 'csp-input--locked': billingSameAsCompany }"
              placeholder="Paris"
              :disabled="billingSameAsCompany"
              @blur="touchSepaField('city')"
            />
            <div v-if="showSepaRequired('city')" class="csp-error small mt-1">Requis</div>
          </div>
        </div>

        <p class="csp-section-label csp-sepa-section text-muted text-uppercase small font-weight-bold mb-2 mt-1">
          Signataire
        </p>

        <div class="form-row">
          <div class="form-group col-md-6 mb-2">
            <label class="csp-label">Nom complet <span class="text-danger">*</span></label>
            <input
              v-model="model.signatory_name"
              type="text"
              class="form-control csp-input"
              placeholder="Thibault Dupont"
              autocomplete="name"
              @blur="touchSepaField('signatory_name')"
            />
            <div v-if="showSepaRequired('signatory_name')" class="csp-error small mt-1">Requis</div>
          </div>
          <div class="form-group col-md-6 mb-2">
            <label class="csp-label">Email <span class="text-danger">*</span></label>
            <input
              v-model="sepaSignatoryEmail"
              type="email"
              class="form-control csp-input"
              placeholder="thibault@aivoni.com"
              autocomplete="email"
              @blur="touchSepaField('signatory_email')"
            />
            <div v-if="showSepaRequired('signatory_email')" class="csp-error small mt-1">Requis</div>
            <p class="text-muted small mt-1 mb-0 csp-sepa-help">
              Une copie du mandat signé sera envoyée à cette adresse
            </p>
          </div>
        </div>

        <div class="csp-mandate-box mb-2">
          <div class="form-check mb-0">
            <input
              id="csp-mandate-accept"
              v-model="mandateAccepted"
              class="form-check-input"
              type="checkbox"
            />
            <label class="form-check-label small csp-mandate-label" for="csp-mandate-accept">
              En signant ce mandat, j'autorise <strong class="text-primary">Oliverlist</strong> à envoyer des
              instructions à ma banque pour débiter mon compte conformément à ce mandat. Je bénéficie d'un droit à
              remboursement par ma banque selon les conditions décrites dans la convention que j'ai passée avec elle.
              Toute demande éventuelle doit être présentée dans les 8 semaines suivant la date de débit.
            </label>
          </div>
        </div>

        <div class="csp-security-box d-flex align-items-start mb-2">
          <i class="icon-shield mr-2 mt-1 text-muted" aria-hidden="true"></i>
          <span class="small text-muted mb-0">
            Vos données bancaires sont chiffrées et stockées de manière conforme PCI-DSS.
          </span>
        </div>

        <span
          class="loadingres d-flex p-2 spinner-border loading-spinner mx-auto"
          v-if="loadingSepaSubmit"
        ></span>

        <div class="csp-footer csp-sepa-footer border-top pt-2 mt-1 d-flex align-items-center justify-content-between">
          <button type="button" class="btn btn-link text-muted p-0 csp-link-cancel" @click="backToStepOne">
            ← Retour
          </button>
          <button
            type="button"
            class="btn csp-btn-continue text-white"
            :disabled="!canSignSepa || loadingSepaSubmit"
            @click="submitSepaMandate"
          >
            Signer le mandat SEPA
          </button>
        </div>
      </div>
    </template>
    </div>
  </div>
</template>

<script>
import Loadingcircle from "@/components/Common/Loadingcircle.vue";
import { billingsService, campaignsService, plansService, proporsalService } from "@/_services";
import { frenchVatError, isValidFrenchVatNumber } from "@/_helpers/frenchVat";
import { mapState, mapActions } from "vuex";

const PRIORITY_ISOS = ["fr", "be", "lu", "ch", "de", "es", "it", "nl", "pt", "gb"];

export default {
  props: {
    selectedMethod: {
      type: Object,
      default() {
        return {};
      },
    },
    subscriptionData: {
      type: Object,
      default() {
        return {};
      },
    },
    title: {
      type: String,
      default: "Save",
    },
    profileData: {
      type: Boolean,
      default: false,
    },
    userId: {
      type: String,
      default: null,
    },
    email: {
      type: String,
      default: null,
    },
    name: {
      type: String,
      default: null,
    },
    client_name: {
      type: String,
      default: null,
    },
    fromProporsal: {
      type: Boolean,
      default: false,
    },
    bank_transfer_enabled: {
      type: Number,
      default: 0,
    },
    bank_transfer_contractual_days: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      signFlowStep: 1,
      billingSameAsCompany: true,
      sepaSignatoryEmail: "",
      mandateAccepted: false,
      loadingSepaSubmit: false,
      visaLogo: require("@/assets/images/visa-logo.png"),
      mcLogo: require("@/assets/images/mc-logo.png"),
      cbLogo: require("@/assets/images/cb-logo.png"),
      paramloading: {
        width: "28px",
        height: "28px",
        color: "#fff",
      },
      btnMethods: {
        sepa: { loading: false, disabled: false },
        creditCard: { loading: false, disabled: false },
        bankTransfer: { loading: false, disabled: true },
      },
      showerror: false,
      paymentMethod: "sepa",
      touched: {
        address: false,
        code_postal: false,
        city: false,
        country_id: false,
        vat_number: false,
      },
      submittedBilling: false,
      touchedSepa: {
        bank_account_name: false,
        iban: false,
        swift_bic: false,
        address: false,
        code_postal: false,
        city: false,
        signatory_name: false,
        signatory_email: false,
      },
      submittedSepa: false,
    };
  },
  computed: {
    model() {
      return this.selectedMethod;
    },
    ibanFormatted() {
      const raw = (this.model.iban || "").replace(/\s/g, "").toUpperCase();
      if (!raw) return "";
      return raw.replace(/(.{4})/g, "$1 ").trim();
    },
    ibanCompactLen() {
      return (this.model.iban || "").replace(/\s/g, "").length;
    },
    isIbanLengthOk() {
      const n = this.ibanCompactLen;
      return n >= 15 && n <= 34;
    },
    ...mapState({
      headerTab: (state) => state.header.tab,
      plans: (state) => state.plans.all,
      activePlans: (state) => state.plans.allActives,
      loading: (state) => state.billings.loading,
      user: (state) => state.authentication.user,
      Countries: (state) => state.optionsList.all["profile_countries"].data,
    }),
    orderedCountries() {
      const list = this.Countries;
      if (!list || !list.length) return [];
      const map = new Map();
      list.forEach((c) => {
        map.set(String(c.iso || "").toLowerCase(), c);
      });
      const priority = [];
      PRIORITY_ISOS.forEach((iso) => {
        const row = map.get(iso);
        if (row) {
          priority.push(row);
          map.delete(iso);
        }
      });
      const rest = Array.from(map.values()).sort((a, b) =>
        this.frenchCountryLabel(a).localeCompare(this.frenchCountryLabel(b), "fr")
      );
      return priority.concat(rest);
    },
    franceId() {
      const fr = this.orderedCountries.find((c) => String(c.iso || "").toLowerCase() === "fr");
      return fr ? String(fr.id) : "73";
    },
    isFrance() {
      return this.model.country_id != null && String(this.model.country_id) === this.franceId;
    },
    showVatError() {
      return this.isFrance && (this.touched.vat_number || this.submittedBilling) && !!frenchVatError(this.model.vat_number);
    },
    frenchVatError() {
      return frenchVatError;
    },
    isFrancePostalValid() {
      if (!this.isFrance) return true;
      const p = (this.model.code_postal || "").trim();
      return /^[0-9]{5}$/.test(p);
    },
    isBillingValid() {
      const addr = (this.model.address || "").trim();
      const city = (this.model.city || "").trim();
      const cp = (this.model.code_postal || "").trim();
      const cid = this.model.country_id;
      if (addr.length < 5 || city.length < 1 || !cid) return false;
      if (this.isFrance) {
        if (!this.isFrancePostalValid) return false;
        if (!isValidFrenchVatNumber(this.model.vat_number)) return false;
      } else {
        if (cp.length < 3) return false;
      }
      return true;
    },
    canContinue() {
      if (this.bank_transfer_enabled == 1) return false;
      if (!this.isBillingValid) return false;
      if (!this.paymentMethod) return false;
      return true;
    },
    isSepaBillingBlockValid() {
      const addr = (this.model.address || "").trim();
      const city = (this.model.city || "").trim();
      const cp = (this.model.code_postal || "").trim();
      if (addr.length < 5 || city.length < 1) return false;
      if (this.isFrance) {
        if (!/^[0-9]{5}$/.test(cp)) return false;
      } else if (cp.length < 3) return false;
      return true;
    },
    isSignatoryEmailFormatOk() {
      const e = (this.sepaSignatoryEmail || "").trim();
      if (!e) return false;
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
    },
    canSignSepa() {
      const company = (this.model.bank_account_name || "").trim();
      if (!company.length) return false;
      if (!this.isIbanLengthOk) return false;
      const bic = (this.model.swift_bic || "").trim();
      if (bic.length < 5) return false;
      const sign = (this.model.signatory_name || "").trim();
      if (sign.length < 2) return false;
      if (!this.isSignatoryEmailFormatOk) return false;
      if (!this.isSepaBillingBlockValid) return false;
      if (!this.mandateAccepted) return false;
      return true;
    },
  },
  watch: {
    Countries: {
      handler() {
        this.applyDefaultCountry();
      },
      immediate: true,
    },
    billingSameAsCompany(val) {
      if (val) {
        this.$set(this.model, "address_line_1", this.model.address || "");
        this.$set(this.model, "postal_code", this.model.code_postal || "");
      }
    },
    "model.name_on_card"() {
      if (this.model.name_on_card != null) {
        this.model.name_on_card = this.$sanitize(this.model.name_on_card);
      }
    },
    "model.card_number"() {
      if (this.model.card_number != null) {
        const cardnumber = this.$sanitize(this.model.card_number);
        this.model.card_number = cardnumber.replace(" ", "");
      }
    },
    "model.cvc"() {
      if (this.model.cvc != null) {
        this.model.cvc = this.$sanitize(this.model.cvc);
      }
    },
  },
  components: {
    Loadingcircle,
  },
  created() {
    window.dataLayer.push({
      event: "Funnel_Paiement",
      stepName: "Chargement Pop-In Paiement",
      stepNumber: "2.3",
    });
    this.checkPlan();
    if (!this.Countries || this.Countries.length === 0) {
      this.$store.dispatch("optionsList/getProfileCountries");
    }
    this.initialDataToForm();
    this.ensureSepaModelKeys();
  },
  methods: {
    ...mapActions("billings", {
      startLoading: "startLoading",
      finishLoading: "finishLoading",
    }),
    ...mapActions("plans", {
      getAllPlans: "getAll",
    }),

    ensureSepaModelKeys() {
      if (this.model.address_line_2 === undefined) {
        this.$set(this.model, "address_line_2", "");
      }
      if (this.model.default === undefined) {
        this.$set(this.model, "default", "1");
      }
    },

    frenchCountryLabel(country) {
      const iso = String(country.iso || "").toUpperCase();
      if (iso.length === 2) {
        try {
          return new Intl.DisplayNames(["fr"], { type: "region" }).of(iso);
        } catch (e) {
          /* ignore */
        }
      }
      return country.name || "";
    },

    applyDefaultCountry() {
      if (!this.orderedCountries.length) return;
      const cur = this.model.country_id;
      if (cur == null || cur === "") {
        this.$set(this.model, "country_id", this.franceId);
      }
    },

    initialDataToForm() {
      if (this.user) {
        if (this.user.client_address) this.$set(this.model, "address", this.user.client_address);
        if (this.user.client_postal_code) {
          this.$set(this.model, "code_postal", this.user.client_postal_code);
        }
        if (this.user.client_city) this.$set(this.model, "city", this.user.client_city);
      }
    },

    touchField(field) {
      this.$set(this.touched, field, true);
    },

    touchSepaField(field) {
      this.$set(this.touchedSepa, field, true);
    },

    showRequired(field) {
      if (!this.touched[field] && !this.submittedBilling) return false;
      if (field === "country_id") {
        return !this.model.country_id;
      }
      if (field === "vat_number") {
        return this.isFrance && !String(this.model.vat_number || "").trim();
      }
      const v = this.model[field];
      if (field === "code_postal") {
        if (!v || !String(v).trim()) return true;
        if (this.isFrance && !this.isFrancePostalValid) return false;
        if (!this.isFrance && String(v).trim().length < 3) return true;
        return false;
      }
      return !v || !String(v).trim();
    },

    showSepaRequired(field) {
      if (!this.touchedSepa[field] && !this.submittedSepa) return false;
      if (field === "iban") {
        if (!this.model.iban || !String(this.model.iban).trim()) return true;
        return !this.isIbanLengthOk;
      }
      if (field === "swift_bic") {
        return !(this.model.swift_bic && String(this.model.swift_bic).trim().length >= 5);
      }
      if (field === "bank_account_name") {
        return !String(this.model.bank_account_name || "").trim().length;
      }
      if (field === "signatory_name") {
        return !(this.model.signatory_name && String(this.model.signatory_name).trim().length >= 2);
      }
      if (field === "signatory_email") {
        return !this.isSignatoryEmailFormatOk;
      }
      if (field === "address") {
        const v = this.model[field];
        return !v || String(v).trim().length < 5;
      }
      if (field === "city") {
        const v = this.model[field];
        return !v || !String(v).trim().length;
      }
      if (field === "code_postal") {
        const cp = (this.model.code_postal || "").trim();
        if (!cp) return true;
        if (this.isFrance) return !/^[0-9]{5}$/.test(cp);
        return cp.length < 3;
      }
      return false;
    },

    onPostalInput() {
      if (!this.isFrance) return;
      const digits = String(this.model.code_postal || "").replace(/\D/g, "").slice(0, 5);
      this.$set(this.model, "code_postal", digits);
    },

    onSepaPostalInput() {
      if (this.billingSameAsCompany || !this.isFrance) return;
      const digits = String(this.model.code_postal || "").replace(/\D/g, "").slice(0, 5);
      this.$set(this.model, "code_postal", digits);
    },

    onCountryChange() {
      if (!this.isFrance) {
        this.$set(this.model, "vat_number", "");
      }
    },

    onVatNumberInput() {
      if (!this.isFrance) return;
      const v = String(this.model.vat_number || "")
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 13);
      this.$set(this.model, "vat_number", v);
    },

    onIbanInput(e) {
      const raw = String(e.target.value || "")
        .replace(/\s/g, "")
        .replace(/[^A-Za-z0-9]/g, "")
        .toUpperCase()
        .slice(0, 34);
      this.$set(this.model, "iban", raw);
    },

    onSwiftInput() {
      const v = String(this.model.swift_bic || "")
        .replace(/[^A-Za-z0-9]/g, "")
        .toUpperCase();
      this.$set(this.model, "swift_bic", v);
    },

    validateProposalBilling(markAllTouched, showSwal = true) {
      if (markAllTouched) {
        this.submittedBilling = true;
        this.touched = {
          address: true,
          code_postal: true,
          city: true,
          country_id: true,
          vat_number: true,
        };
      }
      if (!this.isBillingValid) {
        if (showSwal) {
          this.$swal({
            type: "error",
            title: "Erreur",
            text: "Veuillez corriger les erreurs du formulaire.",
            showConfirmButton: true,
          });
        }
        return false;
      }
      return true;
    },

    prepareSepaStep() {
      this.submittedSepa = false;
      this.touchedSepa = {
        bank_account_name: false,
        iban: false,
        swift_bic: false,
        address: false,
        code_postal: false,
        city: false,
        signatory_name: false,
        signatory_email: false,
      };
      this.mandateAccepted = false;
      this.billingSameAsCompany = true;
      this.$set(this.model, "bank_account_name", (this.client_name || "").trim());
      this.$set(this.model, "iban", "");
      this.$set(this.model, "swift_bic", "");
      this.$set(this.model, "signatory_name", (this.name || "").trim());
      this.sepaSignatoryEmail = (this.email || "").trim();
      this.ensureSepaModelKeys();
      this.$set(this.model, "address_line_1", this.model.address || "");
      this.$set(this.model, "postal_code", this.model.code_postal || "");
    },

    backToStepOne() {
      this.signFlowStep = 1;
      this.submittedSepa = false;
    },

    validateSepaStep(markAll, showSwal = true) {
      if (markAll) {
        this.submittedSepa = true;
        this.touchedSepa = {
          bank_account_name: true,
          iban: true,
          swift_bic: true,
          address: true,
          code_postal: true,
          city: true,
          signatory_name: true,
          signatory_email: true,
        };
      }
      if (!this.canSignSepa) {
        if (showSwal) {
          this.$swal({
            type: "error",
            title: "Erreur",
            text: "Veuillez corriger les erreurs du formulaire.",
            showConfirmButton: true,
          });
        }
        return false;
      }
      return true;
    },

    syncSepaPayloadForSubmit() {
      const iban = String(this.model.iban || "")
        .replace(/\s/g, "")
        .replace(/[^A-Za-z0-9]/g, "")
        .toUpperCase();
      this.$set(this.model, "iban", iban);
      this.$set(this.model, "address_line_1", (this.model.address || "").trim());
      this.$set(this.model, "postal_code", (this.model.code_postal || "").trim());
      this.$set(this.model, "city", (this.model.city || "").trim());
      this.$set(this.model, "swift_bic", (this.model.swift_bic || "").trim().toUpperCase());
      this.$set(this.model, "bank_account_name", (this.model.bank_account_name || "").trim());
      this.$set(this.model, "signatory_name", (this.model.signatory_name || "").trim());
      if (this.model.address_line_2 === undefined || this.model.address_line_2 === null) {
        this.$set(this.model, "address_line_2", "");
      }
      this.$set(this.model, "default", this.model.default != null ? this.model.default : "1");
      this.model.profileData = {
        address: this.model.address,
        code_postal: this.model.code_postal,
        city: this.model.city,
        country_id: this.model.country_id,
        vat_number: this.model.vat_number || null,
      };
      if (this.model.id != null) {
        this.model.profileData.id = this.model.id;
      }
      this.model.isSigning = true;
      if (this.userId) {
        this.model.user_id = this.userId;
        this.model.role_id = 4;
      }
    },

    submitSepaMandate() {
      if (!this.validateSepaStep(true)) return;
      this.syncSepaPayloadForSubmit();
      this.loadingSepaSubmit = true;
      this.startLoading();
      proporsalService
        .setSepaPaymentMethod(this.selectedMethod)
        .then((response) => {
          if (response == "ok") {
            this.closeCreditCardModal();
            this.$nextTick(() => {
              this.$swal({
                type: "success",
                title: "Parfait",
                text: "Moyen de paiement ajouté avec succès",
                showConfirmButton: false,
                timer: 7000,
              }).then(() => {
                window.location.reload();
              });
            });
          } else {
            this.$swal({
              type: "error",
              title: "Erreur",
              html: "Une erreur est survenue.",
              showConfirmButton: false,
              timer: 9000,
            });
          }
        })
        .catch(() => {
          this.$swal({
            type: "error",
            title: "Erreur",
            html: "Une erreur est survenue.",
            showConfirmButton: false,
            timer: 9000,
          });
        })
        .finally(() => {
          this.loadingSepaSubmit = false;
          this.finishLoading();
        });
    },

    onContinue() {
      if (!this.validateProposalBilling(true)) return;
      if (this.paymentMethod === "sepa") {
        this.prepareSepaStep();
        this.signFlowStep = 2;
      } else if (this.paymentMethod === "creditCard") {
        this.showMethods("creditCard");
      }
    },

    optionBankTransfer() {
      if (!this.validateProposalBilling(true)) return;

      this.btnMethods.bankTransfer.loading = true;
      this.btnMethods.bankTransfer.disabled = true;

      const data = Object.assign({});
      data.isSigning = true;
      data.url = this.$route.path;
      data.role_id = 4;
      data.profileData = {};
      data.profileData.address = this.selectedMethod.address;
      data.profileData.country_id = this.selectedMethod.country_id;
      data.profileData.code_postal = this.selectedMethod.code_postal;
      data.profileData.city = this.selectedMethod.city;
      data.profileData.vat_number = this.selectedMethod.vat_number
        ? this.selectedMethod.vat_number
        : null;

      if (this.userId) {
        data.user_id = this.userId;
        proporsalService.saveBankTransferPaymentMethod(data).then(() => {
          window.dataLayer = window.dataLayer || [];
          dataLayer.push({
            event: "Paiement_Enregistré_Parcours_Commercial",
          });
          window.location.reload();
        });
      } else {
        // eslint-disable-next-line no-console
        console.log("it only works from the proposal");
      }
    },

    getModel() {
      if (this.signFlowStep === 2) {
        if (!this.validateSepaStep(true, false)) {
          return false;
        }
        return this.model;
      }
      if (!this.validateProposalBilling(true, false)) {
        return false;
      }
      return this.model;
    },

    checkPlan() {
      plansService.getAll().then((plans) => {
        if (this.subscriptionData.plan_id) {
          const plan = plans.find((p) => {
            return p.plan_id == this.subscriptionData.plan_id;
          });
          if (plan) {
            campaignsService.getActiveCampaigns().then((result) => {
              if (result) {
                if (
                  (plan.plan_id == 1 && result.total > 1) ||
                  (plan.plan_id == 2 && result.total > 3)
                ) {
                  this.$swal({
                    type: "error",
                    title: this.$t("lang.label.error"),
                    text: this.$t("lang.label.thenumberofactivecampaignsishigher"),
                    showConfirmButton: true,
                  });
                  this.$router.push("/upgrade");
                }
              }
            });
          } else {
            this.$swal({
              type: "error",
              title: this.$t("lang.label.error"),
              text: this.$t("lang.label.theplandoesntexists"),
              showConfirmButton: true,
            });
            this.$router.push("/upgrade");
          }
        }
      });
    },

    showMethods(payMethod) {
      if (!this.validateProposalBilling(true)) return;
      this.redirectViva(payMethod);
    },

    redirectViva(payMethod) {
      this.btnMethods.sepa.disabled = true;
      this.btnMethods.creditCard.disabled = true;
      this.btnMethods.bankTransfer.disabled = true;
      this.btnMethods[payMethod].loading = true;

      const data = Object.assign({});
      if (payMethod === "sepa") {
        data.payment_method_type = "gocardless";
      }
      data.url = this.$route.path;
      data.role_id = 4;
      data.address = this.selectedMethod.address;
      data.country_id = this.selectedMethod.country_id;
      data.code_postal = this.selectedMethod.code_postal;
      data.city = this.selectedMethod.city;
      data.vat_number = this.selectedMethod.vat_number ? this.selectedMethod.vat_number : null;

      if (this.userId) {
        data.user_id = this.userId;
        proporsalService.saveSepaPaymentMethod(data).then((response) => {
          window.dataLayer = window.dataLayer || [];
          dataLayer.push({
            event: "Paiement_Enregistré_Parcours_Commercial",
          });
          if (response.orderCode !== undefined) {
            window.location.href = response.orderCode;
          }
        });
      } else {
        billingsService.saveSepaPaymentMethod(data).then((response) => {
          if (response && response.orderCode !== undefined) {
            window.location.href = response.orderCode;
          }
        });
      }
    },

    saveSepaPaymentMethod(termsModel) {
      return new Promise((resolve, reject) => {
        if (termsModel === undefined) {
          termsModel = {};
        }
        const browserInfo = {
          browserColorDepth: screen.colorDepth,
          browserJavaEnabled: navigator.javaEnabled(),
          browserLanguage: navigator.language,
          browserScreenHeight: screen.height,
          browserScreenWidth: screen.width,
          browserTimeZone: new Date().getTimezoneOffset(),
        };

        this.startLoading();
        const data = Object.assign({}, this.model, this.subscriptionData, termsModel, browserInfo);
        data.url = this.$route.path;

        if (this.userId) {
          data.user_id = this.userId;
          data.role_id = 4;
          proporsalService
            .saveSepaPaymentMethod(data)
            .then((response) => {
              window.dataLayer = window.dataLayer || [];
              dataLayer.push({
                event: "Paiement_Enregistré_Parcours_Commercial",
              });
              this.closeCreditCardModal();
              if (response.orderCode !== undefined) {
                window.location.href = response.orderCode;
              } else {
                this.showerror = true;
              }
            })
            .catch(() => {
              this.$swal({
                type: "error",
                title: this.$t("lang.label.error"),
                html: this.$t("lang.label.paymentmethodaddederror"),
                showConfirmButton: false,
                timer: 9000,
              });
              reject("error");
            })
            .finally(() => {
              this.finishLoading();
            });
        } else {
          billingsService
            .saveSepaPaymentMethod(data)
            .then((response) => {
              if (response.orderCode !== undefined) {
                window.location.href = response.orderCode;
              } else {
                this.showerror = true;
              }
            })
            .catch(() => {
              this.$swal({
                type: "error",
                title: this.$t("lang.label.error"),
                html: this.$t("lang.label.paymentmethodaddederror"),
                showConfirmButton: false,
                timer: 9000,
              });
              reject("error");
            });
        }
      });
    },

    refreshPaymentMethods() {
      this.$emit("refreshPaymentMethods", true);
    },

    closeCreditCardModal() {
      this.$emit("closeCreditCardModal", true);
    },

    reloadComponent() {
      this.$emit("reloadComponent", true);
    },
  },
};
</script>

<style scoped>
.contract-sign-proposal {
  max-width: 676px;
  width: 100%;
  margin: 0 auto;
  max-height: min(82vh, calc(100vh - 110px));
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  box-sizing: border-box;
}

.csp-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: 0.95rem 1.075rem;
  margin-top: 2rem;
  margin-right: 0.75rem;
  scrollbar-width: thin;
}

.csp-scroll::-webkit-scrollbar {
  width: 8px;
}

.csp-scroll::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.22);
}

.csp-scroll::-webkit-scrollbar-track {
  background: transparent;
}

@media (max-width: 576px) {
  .csp-scroll {
    padding: 1.35rem 1.1rem;
    margin-top: 1.5rem;
    margin-right: 0.5rem;
  }
}

.csp-badge {
  display: inline-block;
  background: #e3f2fd;
  color: #1565c0;
  font-size: 0.8125rem;
  font-weight: 600;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
}

.csp-badge-sub {
  font-size: 0.85rem;
}

.csp-title {
  font-size: 1.3rem;
  color: #212529;
}

.csp-desc {
  line-height: 1.45;
}

.csp-section-label {
  letter-spacing: 0.04em;
  font-size: 0.75rem;
}

.csp-label {
  font-weight: 600;
  font-size: 0.9375rem;
  color: #343a40;
  margin-bottom: 0.35rem;
}

.csp-input {
  border-radius: 10px;
  border: 1px solid #ced4da;
}

.csp-input--locked:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #e9ecef;
}

.csp-vat-input,
.csp-iban-input {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New",
    monospace;
}

.csp-error {
  color: #dc3545;
}

.csp-pay-card {
  border: 1px solid #dee2e6;
  border-radius: 12px;
  padding: 1.15rem 1.45rem;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.csp-pay-card--outline {
  border-width: 1px;
}

.csp-pay-card--active {
  border: 2px solid #1976d2;
  background: #e3f2fd;
}

.csp-radio {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #adb5bd;
  flex-shrink: 0;
  margin-top: 2px;
  position: relative;
}

.csp-radio--on {
  border-color: #1976d2;
}

.csp-radio--on::after {
  content: "";
  position: absolute;
  top: 3px;
  left: 3px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #1976d2;
}

.csp-reco-badge {
  display: inline-block;
  background: #1976d2;
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.18rem 0.5rem;
  border-radius: 4px;
}

.csp-card-logos .csp-logo-img {
  height: 24px;
  width: auto;
  margin-left: 6px;
  object-fit: contain;
}

.csp-footer {
  border-color: #e9ecef !important;
}

.csp-link-cancel {
  text-decoration: none;
}

.csp-link-cancel:hover {
  text-decoration: underline;
}

.csp-btn-continue {
  background: #212529;
  border: none;
  border-radius: 10px;
  padding: 0.55rem 1.45rem;
  font-weight: 600;
}

.csp-btn-continue:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.csp-info-box {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 14px;
  padding: 1.1rem 1.35rem;
}

.csp-security-box {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 14px;
  padding: 1.1rem 1.35rem;
}

.csp-mandate-box {
  background: #e3f2fd;
  border: 1px solid #1976d2;
  border-radius: 14px;
  padding: 1.2rem 1.4rem;
}

.csp-mandate-label {
  line-height: 1.45;
}

/* Étape SEPA : champs compacts */
.csp-sepa-title {
  font-size: 1.18rem;
}

.csp-sepa-section {
  line-height: 1.2;
}

.csp-sepa .csp-info-box,
.csp-sepa .csp-security-box {
  padding: 0.6rem 0.95rem;
}

.csp-sepa .csp-mandate-box {
  padding: 0.72rem 0.95rem;
}

.csp-sepa .csp-mandate-label {
  line-height: 1.32;
  font-size: 0.84rem;
}

.csp-sepa-help {
  line-height: 1.25;
  font-size: 0.82rem;
}

.csp-sepa .form-control {
  padding-top: 0.4rem;
  padding-bottom: 0.4rem;
  min-height: 0;
}
</style>
